import type { CadenceRequirement, ExpectedSlot, MonthlyCommitment, WorkspaceWeek } from "./planningModel";
import { commitmentForType } from "./planningModel";
import type { ContentType, Weekday } from "./types";

const MAX_ACTIVE_SLOTS = 200;
const contentTypes: ContentType[] = ["story", "post", "reel"];
const weekdayByIndex: Weekday[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export function generateExpectedSlots({ month, commitment, cadence, enforceSafetyCap = true }: {
  month: string;
  commitment: MonthlyCommitment;
  cadence: CadenceRequirement[];
  enforceSafetyCap?: boolean;
}): { expectedSlots: ExpectedSlot[]; skippedCadenceNotes: string[]; weeks: WorkspaceWeek[] } {
  assertMonth(month);
  const totalCommitment = commitment.stories + commitment.posts + commitment.reels;
  if (enforceSafetyCap && totalCommitment > MAX_ACTIVE_SLOTS) {
    throw new Error(
      `ODC_EXPECTED_SLOTS_002: Monthly commitment exceeds the aggregate safety cap. function=expectedSlots.generateExpectedSlots field=commitment observed=${totalCommitment} expected<=${MAX_ACTIVE_SLOTS}. Next: reduce the monthly commitment before generating slots.`,
    );
  }

  const dates = datesInMonth(month);
  const fullWeekDates = new Set(dates.filter((date) => isFullWeekInMonth(date, month)).map(formatDate));
  const expectedSlots: ExpectedSlot[] = [];
  const skippedCadenceNotes: string[] = [];

  for (const contentType of contentTypes) {
    const committed = commitmentForType(commitment, contentType);
    const occurrences = dates.flatMap((date) =>
      cadence
        .filter((entry) => entry.contentType === contentType && entry.weekday === weekdayByIndex[date.getUTCDay()])
        .flatMap((entry) => Array.from({ length: Math.max(0, Math.floor(entry.quantity)) }, (_, occurrence) => ({
          date: formatDate(date),
          occurrence,
          fullWeek: fullWeekDates.has(formatDate(date)),
        }))),
    );
    const selected = selectOccurrences(occurrences, committed);
    selected.forEach((occurrence, index) => {
      expectedSlots.push({
        id: `slot-${contentType}-${String(index + 1).padStart(2, "0")}`,
        contentType,
        scheduledDate: occurrence.date,
        state: "active",
        source: "cadence",
      });
    });
    const remainderDates = selectRemainderDates(
      dates.map(formatDate),
      selected.map((occurrence) => occurrence.date),
      Math.max(committed - selected.length, 0),
    );
    for (let index = selected.length; index < committed; index += 1) {
      expectedSlots.push({
        id: `slot-${contentType}-${String(index + 1).padStart(2, "0")}`,
        contentType,
        scheduledDate: remainderDates[index - selected.length],
        state: "active",
        source: "commitment_remainder",
      });
    }
    const skipped = Math.max(occurrences.length - selected.length, 0);
    if (skipped) {
      skippedCadenceNotes.push(`${skipped} ${contentType} cadence ${skipped === 1 ? "occurrence was" : "occurrences were"} skipped to match commitment.`);
    }
  }

  return { expectedSlots, skippedCadenceNotes, weeks: buildWorkspaceWeeks(month) };
}

/** Count actual weekday occurrences in the selected calendar month. This is the
 * commitment source for configuration previews; it intentionally does not use
 * weekly-total-times-four arithmetic. */
export function deriveMonthlyCommitmentFromCadence(month: string, cadence: CadenceRequirement[]): MonthlyCommitment {
  assertMonth(month);
  const totals: MonthlyCommitment = { stories: 0, posts: 0, reels: 0 };
  for (const date of datesInMonth(month)) {
    const weekdayName = weekdayByIndex[date.getUTCDay()];
    for (const entry of cadence) {
      if (entry.weekday !== weekdayName) continue;
      const key = entry.contentType === "story" ? "stories" : entry.contentType === "post" ? "posts" : "reels";
      totals[key] += Math.max(0, Math.floor(entry.quantity));
    }
  }
  return totals;
}

export function deriveWeeklyCommitmentFromCadence(cadence: CadenceRequirement[]): MonthlyCommitment {
  return cadence.reduce<MonthlyCommitment>((totals, entry) => {
    const key = entry.contentType === "story" ? "stories" : entry.contentType === "post" ? "posts" : "reels";
    totals[key] += Math.max(0, Math.floor(entry.quantity));
    return totals;
  }, { stories: 0, posts: 0, reels: 0 });
}

export function getCadencePreviewSlots(month: string, cadence: CadenceRequirement[]): ExpectedSlot[] {
  const commitment = deriveMonthlyCommitmentFromCadence(month, cadence);
  return generateExpectedSlots({ month, commitment, cadence, enforceSafetyCap: false }).expectedSlots;
}

function selectRemainderDates(allDates: string[], usedDates: string[], count: number) {
  if (count <= 0) return [];
  const used = new Set(usedDates);
  const available = allDates.filter((date) => !used.has(date));
  if (!available.length) {
    return Array.from({ length: count }, (_, index) => allDates[index % Math.max(allDates.length, 1)]);
  }
  if (available.length <= count) {
    return [...available, ...Array.from({ length: count - available.length }, (_, index) => available[index % available.length])];
  }
  return evenSubset(available, count);
}

export function buildWorkspaceWeeks(month: string): WorkspaceWeek[] {
  assertMonth(month);
  const [year, monthNumber] = month.split("-").map(Number);
  const first = new Date(Date.UTC(year, monthNumber - 1, 1));
  const last = new Date(Date.UTC(year, monthNumber, 0));
  const firstMonday = addDays(first, -mondayOffset(first));
  const lastSunday = addDays(last, 6 - mondayOffset(last));
  const weeks: WorkspaceWeek[] = [];
  for (let start = firstMonday, week = 1; start <= lastSunday; start = addDays(start, 7), week += 1) {
    weeks.push({
      id: `week-${week}`,
      week,
      startsOn: formatDate(start),
      endsOn: formatDate(addDays(start, 6)),
      holidayLabels: [],
    });
  }
  return weeks;
}

function selectOccurrences<T extends { fullWeek: boolean }>(occurrences: T[], count: number): T[] {
  if (count <= 0) return [];
  if (occurrences.length <= count) return occurrences;
  const full = occurrences.filter((occurrence) => occurrence.fullWeek);
  if (full.length >= count) return evenSubset(full, count);
  const boundary = occurrences.filter((occurrence) => !occurrence.fullWeek);
  return [...full, ...evenSubset(boundary, count - full.length)].sort(
    (left: any, right: any) => String(left.date).localeCompare(String(right.date)) || left.occurrence - right.occurrence,
  );
}

function evenSubset<T>(values: T[], count: number): T[] {
  if (count <= 0) return [];
  if (count === 1) return [values[Math.floor((values.length - 1) / 2)]];
  return Array.from({ length: count }, (_, index) => values[Math.round((index * (values.length - 1)) / (count - 1))]);
}

function datesInMonth(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return Array.from({ length: lastDay }, (_, index) => new Date(Date.UTC(year, monthNumber - 1, index + 1)));
}

function isFullWeekInMonth(date: Date, month: string) {
  const monday = addDays(date, -mondayOffset(date));
  return formatDate(monday).startsWith(month) && formatDate(addDays(monday, 6)).startsWith(month);
}

function mondayOffset(date: Date) { return (date.getUTCDay() + 6) % 7; }
function addDays(date: Date, days: number) { const next = new Date(date); next.setUTCDate(next.getUTCDate() + days); return next; }
function formatDate(date: Date) { return date.toISOString().slice(0, 10); }
function assertMonth(month: string) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
    throw new Error(`ODC_EXPECTED_SLOTS_001: Invalid month. function=expectedSlots.generateExpectedSlots field=month observed=${month} expected=YYYY-MM. Next: choose a valid proposal month.`);
  }
}
